/**
 * Get the profile URL for a user
 * Uses profileSlug if available, otherwise falls back to _id
 * @param {Object} user - User object with _id and optionally profileSlug
 * @returns {string} - Profile URL path
 */
export const getUserId = (user) => {
  if (!user) return '';
  const id = user._id ?? user.id;
  return id != null ? String(id) : '';
};

/**
 * Normalize auth user objects so `_id` is always set (login/register return `id`).
 */
export const normalizeAuthUser = (user) => {
  if (!user) return null;
  const id = user._id ?? user.id;
  if (!id) return user;
  if (user._id) return user;
  return { ...user, _id: id };
};

export const getProfileUrl = (user) => {
  if (!user) return '#';
  
  // Use profileSlug if available, otherwise use _id
  if (user.profileSlug) {
    return `/user/${user.profileSlug}`;
  }
  
  // Fallback to _id
  if (user._id) {
    return `/user/${user._id}`;
  }
  
  return '#';
};

/**
 * Debug function to log user profile URL generation
 * @param {Object} user - User object
 * @param {string} source - Where this is being called from
 */
export const debugProfileUrl = (user, source = 'unknown') => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[ProfileURL Debug - ${source}]`, {
      hasUser: !!user,
      hasProfileSlug: !!user?.profileSlug,
      profileSlug: user?.profileSlug,
      hasId: !!user?._id,
      id: user?._id,
      displayName: user?.displayName,
      username: user?.username,
      finalUrl: getProfileUrl(user)
    });
  }
};

/**
 * Check if a user has buyer capabilities
 * Users can have buyer role even if their primary userType is 'creator' or 'admin'
 * @param {Object} user - User object with userType and optionally roles array
 * @returns {boolean} - True if user has buyer capabilities
 */
export const hasBuyerCapability = (user) => {
  if (!user) return false;
  
  // Check if primary userType is buyer
  if (user.userType === 'buyer') return true;
  
  // Creators and admins can also buy and see their purchases (e.g. bought before becoming creator)
  if (user.userType === 'creator' || user.userType === 'admin') return true;
  
  // Check if user has an active buyer role in roles array
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some(role => 
      role.type === 'buyer' && 
      role.isActive !== false && 
      role.buyerApprovalStatus !== 'rejected'
    );
  }
  
  return false;
};
